import { useState, useEffect, useRef } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

// Best-effort "no download" viewer for an assigned PDF/video. Honest limit (stated once,
// here, not re-litigated at every call site): nothing client-side can stop a device from
// photographing or screen-recording its own display — that's inherent to serving a
// human-viewable file at all, not a gap in this component.
//
// What's actually real:
//   - Every link comes from a short-lived (5 min) signed URL
//     (get-curriculum-asset-url Edge Function), hides the browser's native
//     download/print controls where the platform allows it, and blocks the right-click
//     context menu — deterrents, not locks; none of this stops someone determined.
//   - PDF: the signed URL itself now points at a per-request copy with the viewer's
//     email burned into the actual page content server-side (get-curriculum-asset-url) —
//     this survives a save/download/screenshot/photo, because it's part of the file, not
//     a client-side overlay. The floating overlay below is a second, redundant layer on
//     top of that, not the real protection for PDFs.
//   - Video: there is no server-side equivalent (burning a watermark into video frames
//     needs transcoding infrastructure this project doesn't have) — the floating overlay
//     below is the ONLY watermark a video gets, and it only helps against the realistic
//     threat for video specifically: screen-recording, where the overlay IS captured in
//     the recording. It moves periodically so a recording can't just crop a static corner
//     to remove it.
//   - Video fullscreen: ANY path that puts the <video> ELEMENT ITSELF into fullscreen
//     (not our wrapping container) silently leaves the watermark — a sibling <div>, not
//     inside the video — behind. There is more than one such path: the native toolbar
//     button (hidden via controlsList='nofullscreen', Chrome/Edge only — Firefox has no
//     controlsList support at all), but ALSO a plain double-click on the video, which
//     Chrome/Edge handle as a built-in native gesture independent of controlsList
//     entirely — confirmed the hard way: the toolbar button was hidden and a custom
//     button added, and the watermark still vanished because double-click was never
//     accounted for. Defended two ways now: (1) a dblclick handler on the video calls
//     preventDefault(), which Chrome honors as cancelling its built-in double-click
//     fullscreen; (2) a fullscreenchange listener that — regardless of WHAT gesture
//     caused it — detects the video itself (not the container) went fullscreen and
//     immediately swaps to making the container fullscreen instead. (2) is the real
//     safety net; (1) just avoids the visible flicker of swapping on the common case.
interface SecureAssetViewerProps {
  assetId: string
  assetType: 'pdf' | 'video'
  title: string
}

const WATERMARK_POSITIONS = [
  { bottom: '8%', right: '4%' }, { top: '8%', right: '4%' },
  { bottom: '8%', left: '4%' }, { top: '8%', left: '4%' },
  { bottom: '45%', right: '4%' },
]

export function SecureAssetViewer({ assetId, assetType, title }: SecureAssetViewerProps) {
  const { user, profile } = useAuth()
  const [url, setUrl] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [posIndex, setPosIndex] = useState(0)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const videoRef = useRef<HTMLVideoElement | null>(null)

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      const { data, error: fnErr } = await supabase.functions.invoke('get-curriculum-asset-url', {
        body: { assetId },
      })
      if (cancelled) return
      if (fnErr || data?.error) { setError(data?.error ?? 'Could not load this file.'); setLoading(false); return }
      setUrl(data.url)
      setLoading(false)
    }
    load()
    return () => { cancelled = true }
  }, [assetId])

  // Video only: cycle the overlay's position every few seconds so a screen recording
  // can't defeat it just by cropping out one fixed corner.
  useEffect(() => {
    if (assetType !== 'video') return
    intervalRef.current = setInterval(() => {
      setPosIndex(i => (i + 1) % WATERMARK_POSITIONS.length)
    }, 4000)
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [assetType])

  useEffect(() => {
    if (assetType !== 'video') return
    const onChange = () => {
      const fsEl = document.fullscreenElement
      setIsFullscreen(fsEl === containerRef.current)
      // Safety net for any native path that fullscreens the <video> itself instead of
      // our container (double-click, a remaining native control, a keyboard shortcut —
      // whatever it was), regardless of whether it was anticipated above: swap to the
      // container so the watermark is never left behind.
      if (fsEl && fsEl === videoRef.current) {
        document.exitFullscreen().then(() => containerRef.current?.requestFullscreen()).catch(() => {})
      }
    }
    document.addEventListener('fullscreenchange', onChange)
    return () => document.removeEventListener('fullscreenchange', onChange)
  }, [assetType])

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    } else {
      containerRef.current?.requestFullscreen()
    }
  }

  if (loading) return <div style={{ padding: 24, color: 'rgba(27,42,74,0.5)' }}>Loading…</div>
  if (error || !url) return <div className='banner-error'>{error || 'Could not load this file.'}</div>

  const watermarkLabel = [profile?.full_name, user?.email].filter(Boolean).join(' · ') || user?.email || ''

  return (
    <div
      ref={containerRef}
      style={{
        position: 'relative', background: '#1B2A4A',
        borderRadius: isFullscreen ? 0 : 12, overflow: 'hidden',
        width: isFullscreen ? '100vw' : undefined, height: isFullscreen ? '100vh' : undefined,
        display: isFullscreen ? 'flex' : undefined, alignItems: isFullscreen ? 'center' : undefined,
      }}
      onContextMenu={e => e.preventDefault()}
    >
      {assetType === 'pdf' ? (
        <iframe
          title={title}
          src={`${url}#toolbar=0&navpanes=0`}
          style={{ width: '100%', height: '70vh', border: 'none', display: 'block' }}
        />
      ) : (
        <>
          <video
            ref={videoRef}
            src={url}
            controls
            controlsList='nodownload noremoteplayback nofullscreen'
            disablePictureInPicture
            onDoubleClick={e => e.preventDefault()}
            style={{ width: '100%', maxHeight: isFullscreen ? '100vh' : '70vh', display: 'block' }}
          />
          <button
            onClick={toggleFullscreen}
            title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
            style={{
              position: 'absolute', bottom: 46, right: 10, zIndex: 2,
              width: 30, height: 30, borderRadius: 6, border: 'none',
              background: 'rgba(0,0,0,0.55)', color: '#fff', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', font: '600 13px Inter, sans-serif',
            }}
          >
            {isFullscreen ? '⤡' : '⤢'}
          </button>
        </>
      )}
      {/* Floating watermark. For video this is the ONLY watermark (see header comment) —
          position cycles so it can't be cropped out of a screen recording. For PDF it's
          redundant with the server-baked one, kept only as an on-screen reminder. */}
      <div style={{
        position: 'absolute', pointerEvents: 'none',
        font: '500 11px Inter, sans-serif', color: 'rgba(255,255,255,0.4)',
        textShadow: '0 1px 2px rgba(0,0,0,0.4)',
        transition: 'all 0.6s ease',
        ...(assetType === 'video' ? WATERMARK_POSITIONS[posIndex] : { bottom: '10px', right: '14px' }),
      }}>
        {watermarkLabel}
      </div>
    </div>
  )
}
